import { Box, Text } from '@ksyos/design-system';
import { getProgrammeList } from '@frontend/components/pages/episodeCategory/therapielandProgrammes/getProgrammes';
import type { ICustomControlRequestReadProps } from '../../../interfaces/customControlRequestReadProps';
import type { IOsasvcV2CustomControlData } from './OsasvcV2RequestAddEdit';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { getDisplayName } from '../../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../../translate';
import { ContactReadButtons } from '../../../../../elements/carePathButtons/ContactReadButtons';
import { FormParagraph, HeaderDivider, UserTextParagraph } from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { List, ListItem } from '../../../../../elements/List/List';
import { NullableFormattedDate } from '../../../../../elements/NullableFormattedDate';
import { FormattedDate } from '../../../../../userDataManagement/FormattedDate';
import { ProcessContainer } from '../../../../episodes/common';
import { ProcessItemHeader } from '../../../../episodes/ProcessItemHeader';
import { getToesturenValues } from '../../../OSASC2/v2/getToesturenValues';
import { OsasAddendumAdd } from '../../../utils/v1/addendum/OsasAddendumAdd';
import { OsasAddendumRead } from '../../../utils/v1/addendum/OsasAddendumRead';
import { RequestThumbnails } from '../../../utils/v1/RequestThumbnails';
import { OsasvcV2QuestionnairesScoresRead } from './OsasvcV2QuestionnairesScoresRead';

export function OsasvcV2RequestRead(props: ICustomControlRequestReadProps) {
    const date = props.process.dateSent || (props.request && props.request.dateSent);

    const customControlData: IOsasvcV2CustomControlData = props.request.customControlData;

    const toesturenValues =
        getProgrammeList(customControlData.checkedTreatments) +
        getToesturenValues(customControlData.toesturen_therapieland_modules);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={date}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({ ...props.itemState, isOpen: isOpened });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            {customControlData.date_contact && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('request.dateContact')}</FormLabel>
                    <InputContainer>
                        <FormParagraph bold>
                            <NullableFormattedDate value={customControlData.date_contact} />
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.anamnese && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('carePlan.anamnese')}</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{customControlData.anamnese}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.onderzoek && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('procedure.procedure')}</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{customControlData.onderzoek}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            <OsasvcV2QuestionnairesScoresRead questionnaires={customControlData.questionnaires} />

            {customControlData.nieuw_slaaponderzoek !== null && (
                <>
                    <HorizontalFormGroupRead>
                        <FormLabel>Nieuw slaaponderzoek</FormLabel>
                        <InputContainer>
                            <FormParagraph>{customControlData.nieuw_slaaponderzoek ? 'Ja' : 'Nee'}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                </>
            )}
            {customControlData.nieuw_slaaponderzoek_start_date && customControlData.nieuw_slaaponderzoek_end_date && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('insurers.periodOfCareLimitation')}</FormLabel>
                    <Box
                        display="flex"
                        gap={2}
                    >
                        <FormattedDate value={customControlData.nieuw_slaaponderzoek_start_date} />
                        <Text
                            as="small"
                            mt={2.5}
                            size="xs"
                            sx={{
                                display: 'block',
                                height: '25px',
                                width: '100%',
                            }}
                            textAlign="center"
                        >
                            {translateKey('common.until')}
                        </Text>
                        <FormattedDate value={customControlData.nieuw_slaaponderzoek_end_date} />
                    </Box>
                </HorizontalFormGroupRead>
            )}

            {customControlData.gemiddeldeAhi !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde AHI</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.gemiddeldeAhi}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.gemiddeldeOdi !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde ODI 3%</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.gemiddeldeOdi}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.odi4 !== null && customControlData.odi4 !== undefined && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde ODI 4%</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.odi4}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.gemiddeldeSaturatie !== null && customControlData.gemiddeldeSaturatie !== undefined && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde saturatie %</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.gemiddeldeSaturatie}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.laagstGemetenSaturatie !== null &&
                customControlData.laagstGemetenSaturatie !== undefined && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Laagst gemeten saturatie %</FormLabel>
                        <InputContainer>
                            <FormParagraph>{customControlData.laagstGemetenSaturatie}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

            {customControlData.relevanteHypoxemie !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Relevante Hypoxemie</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.relevanteHypoxemie ? 'Ja' : 'Nee'}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.relevanteHypoxemieRemarks !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.relevanteHypoxemieRemarks}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.positieafhankelijk !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Positieafhankelijk</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.positieafhankelijk}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.positieafhankelijkRemarks !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.positieafhankelijkRemarks}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {showRelevanteBevindingen()}

            {customControlData.conclusie && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('common.conclusion')}</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{customControlData.conclusie}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            <RequestThumbnails {...props} />

            {customControlData.therapie_gestart !== null && (
                <>
                    <HeaderDivider text="Therapie beleid" />
                    <HorizontalFormGroupRead>
                        <FormLabel>Therapie gestart</FormLabel>
                        <InputContainer>
                            <FormParagraph>{customControlData.therapie_gestart ? 'Ja' : 'Nee'}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                </>
            )}

            {customControlData.therapie_gestart_namelijk !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Namelijk</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.therapie_gestart_namelijk}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.therapie !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Therapie</FormLabel>
                    <InputContainer>
                        <FormParagraph>{customControlData.therapie}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.betreft_proefperiode !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Betreft proefperiode</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {customControlData.betreft_proefperiode ? 'Ja, de proefperiode wordt beëindigd' : 'Nee'}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.nieuwe_therapie_starten !== null && (
                <>
                    <HeaderDivider text="Nieuwe therapie starten" />
                    <HorizontalFormGroupRead>
                        <FormLabel>Nieuwe therapie starten</FormLabel>
                        <InputContainer>
                            <FormParagraph>{customControlData.nieuwe_therapie_starten ? 'Ja' : 'Nee'}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                </>
            )}

            {customControlData.nieuwe_therapie_starten_namelijk && (
                <HorizontalFormGroupRead>
                    <FormLabel>Namelijk</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {customControlData.nieuwe_therapie_starten_namelijk}{' '}
                            {customControlData.nieuwe_therapie_starten_mra_abcd_score
                                ? ` (${customControlData.nieuwe_therapie_starten_mra_abcd_score})`
                                : ''}
                        </FormParagraph>
                        {customControlData.cpap_instellingen && (
                            <FormParagraph>{customControlData.cpap_instellingen}</FormParagraph>
                        )}
                        {customControlData.cpap_aangepaste_instellingen_namelijk && (
                            <FormParagraph>{customControlData.cpap_aangepaste_instellingen_namelijk}</FormParagraph>
                        )}

                        {customControlData.nieuwe_therapie_starten_bipap_instellingen && (
                            <FormParagraph>
                                {customControlData.nieuwe_therapie_starten_bipap_instellingen}
                            </FormParagraph>
                        )}
                        {customControlData.nieuwe_therapie_starten_toelichting && (
                            <FormParagraph>{customControlData.nieuwe_therapie_starten_toelichting}</FormParagraph>
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {shouldDisplayAanvullendeAfspraken() && <HeaderDivider text="Aanvullende afspraken" />}
            {(customControlData.aanvullend_beleid.advies_lifestyle ||
                customControlData.aanvullend_beleid.anders ||
                toesturenValues) && (
                <HorizontalFormGroupRead>
                    <FormLabel>Aanvullend beleid</FormLabel>
                    <InputContainer>
                        <List readVariant="stacked">
                            {customControlData.aanvullend_beleid.advies_lifestyle && (
                                <ListItem bold>
                                    Advies lifestyle{' '}
                                    {customControlData.aanvullend_beleid.advies_lifestyle_namelijk
                                        ? `: ${customControlData.aanvullend_beleid.advies_lifestyle_namelijk}`
                                        : ''}
                                </ListItem>
                            )}
                            {!!toesturenValues && (
                                <ListItem bold>{'E-health programma´s: ' + toesturenValues}</ListItem>
                            )}
                            {customControlData.aanvullend_beleid.anders && (
                                <ListItem bold>{customControlData.aanvullend_beleid.anders}</ListItem>
                            )}
                        </List>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.vervolg_afspraak_met_specialist_nieuw_slaaponderzoek && (
                <HorizontalFormGroupRead>
                    <FormLabel>Vervolgafspraak met specialist</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {customControlData.vervolg_afspraak_met_specialist_nieuw_slaaponderzoek}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {(customControlData.vervolg_afspraak_met_specialist.volgens_protocol ||
                customControlData.vervolg_afspraak_met_specialist.anders) && (
                <HorizontalFormGroupRead>
                    <FormLabel>Vervolg afspraak met specialist</FormLabel>
                    <InputContainer>
                        <List readVariant="stacked">
                            {customControlData.vervolg_afspraak_met_specialist.volgens_protocol && (
                                <ListItem bold>Volgens protocol</ListItem>
                            )}
                            {customControlData.vervolg_afspraak_met_specialist.anders && (
                                <ListItem bold>{customControlData.vervolg_afspraak_met_specialist.anders}</ListItem>
                            )}
                        </List>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.advies_aan_de_huisarts && (
                <HorizontalFormGroupRead>
                    <FormLabel>Advies aan de huisarts...</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{customControlData.advies_aan_de_huisarts}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.toelichting && (
                <HorizontalFormGroupRead>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{customControlData.toelichting}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            <OsasAddendumRead addendum={customControlData?.addendum} />

            <OsasAddendumAdd
                processEntity={ProcessEntity.request}
                reloadRequest={props.reloadRequest}
                request={props.request}
            />

            <ContactReadButtons {...props} />
        </ProcessContainer>
    );

    function shouldDisplayAanvullendeAfspraken() {
        return (
            customControlData.aanvullend_beleid.advies_lifestyle ||
            customControlData.aanvullend_beleid.advies_lifestyle_namelijk ||
            (customControlData.toesturen_therapieland_modules &&
                Object.values(customControlData.toesturen_therapieland_modules).some((field) => field)) ||
            customControlData.checkedTreatments?.length ||
            customControlData.vervolg_afspraak_met_specialist.volgens_protocol ||
            customControlData.vervolg_afspraak_met_specialist.anders ||
            customControlData.vervolg_afspraak_met_specialist_nieuw_slaaponderzoek ||
            customControlData.advies_aan_de_huisarts ||
            customControlData.toelichting
        );
    }

    function showRelevanteBevindingen() {
        const values: string[] = [];
        if (customControlData.inefficienteSlaaphygiene) {
            values.push(
                'Inefficiënte slaaphygiëne' +
                    (customControlData.inefficienteSlaaphygieneRemarks
                        ? `: ${customControlData.inefficienteSlaaphygieneRemarks}`
                        : '')
            );
        }
        if (customControlData.veelBewegingen) {
            values.push('Veel bewegingen');
        }
        if (customControlData.slechteSlaapefficientie) {
            values.push('Niet optimale slaapefficiëntie');
        }
        if (customControlData.afwijkendHartritme) {
            values.push(
                'Afwijkend hartritme' +
                    (customControlData.afwijkendHartritmeRemarks
                        ? `: ${customControlData.afwijkendHartritmeRemarks}`
                        : '')
            );
        }
        if (customControlData.overigeRelevanteBevindingenAndersText) {
            values.push(customControlData.overigeRelevanteBevindingenAndersText);
        }

        if (values.length > 0) {
            return (
                <HorizontalFormGroupRead>
                    <FormLabel>Overige relevante bevindingen</FormLabel>
                    <InputContainer>
                        <FormParagraph>{values.join(', ')}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            );
        }
    }
}
